import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  InputAdornment,
  TextField,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  Bolt as JumpIcon,
  KeyboardReturn as EnterIcon,
  Person as CustomerIcon,
  ShoppingCart as SalesOrderIcon,
  Receipt as InvoiceIcon,
  RequestQuote as QuotationIcon,
  Description as DocumentIcon,
  Inventory2 as MaterialIcon,
  EmojiEvents as OpportunityIcon,
  PersonSearch as LeadIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { searchService } from '../../services/api';
import {
  performSearch,
  selectSearchResults,
  selectIsSearching,
  selectSearchHistory,
} from '../../redux/slices/searchSlice';
import { getResultClickAction } from '../../utils/searchUtils';

const RESULT_ICON = {
  customer: <CustomerIcon fontSize="small" />,
  lead: <LeadIcon fontSize="small" />,
  opportunity: <OpportunityIcon fontSize="small" />,
  quotation: <QuotationIcon fontSize="small" />,
  sales_order: <SalesOrderIcon fontSize="small" />,
  invoice: <InvoiceIcon fontSize="small" />,
  material: <MaterialIcon fontSize="small" />,
  document: <DocumentIcon fontSize="small" />,
  product_document: <MaterialIcon fontSize="small" />,
};

const iconFor = (type) => RESULT_ICON[type] || <DocumentIcon fontSize="small" />;

/**
 * Full-screen Ctrl+K / Cmd+K spotlight search overlay.
 *
 * Shows (in order): Jump-To banner (if query matches a record pattern),
 * recent history (when empty), and top results. Arrow keys / Enter navigate.
 */
const SearchSpotlightModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const results = useSelector(selectSearchResults);
  const isSearching = useSelector(selectIsSearching);
  const searchHistory = useSelector(selectSearchHistory);

  const [query, setQuery] = useState('');
  const [jumpTo, setJumpTo] = useState(null);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setJumpTo(null);
      setHighlightIdx(0);
      // Autofocus after dialog mounts
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  const runSearch = useMemo(
    () =>
      debounce((q) => {
        if (!q || q.trim().length < 2) return;
        dispatch(
          performSearch({
            query: q.trim(),
            modules: ['core', 'mm', 'crm'],
            offset: 0,
            limit: 10,
            useCache: true,
          })
        );
      }, 250),
    [dispatch]
  );

  const fetchJumpTo = useMemo(
    () =>
      debounce(async (q) => {
        if (!q || q.trim().length < 2) {
          setJumpTo(null);
          return;
        }
        try {
          const res = await searchService.getSuggestions(q.trim());
          setJumpTo(res.data?.jump_to || null);
        } catch {
          setJumpTo(null);
        }
      }, 200),
    []
  );

  useEffect(() => {
    runSearch(query);
    fetchJumpTo(query);
    setHighlightIdx(0);
    return () => {
      runSearch.cancel();
      fetchJumpTo.cancel();
    };
  }, [query, runSearch, fetchJumpTo]);

  // Build the ordered "selectable list" so Arrow keys / Enter can act on it
  const flatItems = useMemo(() => {
    const items = [];
    if (jumpTo) items.push({ kind: 'jump', data: jumpTo });
    if (!query && searchHistory?.length) {
      searchHistory.slice(0, 5).forEach((h) => items.push({ kind: 'history', data: h }));
    }
    results.forEach((r) => items.push({ kind: 'result', data: r }));
    return items;
  }, [jumpTo, query, searchHistory, results]);

  const handleActivate = useCallback(
    (item) => {
      if (!item) return;
      if (item.kind === 'jump') {
        navigate(item.data.navigate_to);
        onClose();
        return;
      }
      if (item.kind === 'history') {
        setQuery(item.data.query);
        return;
      }
      if (item.kind === 'result') {
        const action = getResultClickAction(item.data);
        if (action.type === 'navigate') {
          navigate(action.url);
        } else if (action.type === 'document_view') {
          navigate(`/document/${action.documentId}`);
        } else if (action.type === 'file_url' || action.type === 'admin_url') {
          window.open(action.url, '_blank');
        }
        onClose();
      }
    },
    [navigate, onClose]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, Math.max(flatItems.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[highlightIdx]) {
        handleActivate(flatItems[highlightIdx]);
      } else if (query.trim()) {
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Cluster results by source_module so cross-module grouping is visible
  const clusters = useMemo(() => {
    const byModule = {};
    results.forEach((r) => {
      const key = r.source_module || 'other';
      if (!byModule[key]) byModule[key] = [];
      byModule[key].push(r);
    });
    return byModule;
  }, [results]);

  // Index offset helper so highlighting picks the right flat item
  let flatIdx = 0;
  if (jumpTo) flatIdx += 1;
  if (!query && searchHistory?.length) flatIdx += Math.min(searchHistory.length, 5);
  const firstResultIdx = flatIdx;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, mt: 8, overflow: 'hidden' } }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Search SOs, customers, invoices, documents…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={18} />
                ) : (
                  <SearchIcon color="action" />
                )}
              </InputAdornment>
            ),
            sx: { fontSize: '1.1rem' },
          }}
        />
        <Chip label="Esc" size="small" variant="outlined" />
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent sx={{ p: 0, minHeight: 240, maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Jump-To banner */}
        {jumpTo && (
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <ListItemButton
              selected={highlightIdx === 0}
              onClick={() => handleActivate({ kind: 'jump', data: jumpTo })}
              sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <JumpIcon />
              </ListItemIcon>
              <ListItemText
                primary={jumpTo.label}
                secondary={jumpTo.navigate_to}
                secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.8)' } }}
              />
              <EnterIcon fontSize="small" />
            </ListItemButton>
          </Box>
        )}

        {/* History (only when no query) */}
        {!query && searchHistory?.length > 0 && (
          <>
            <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Recent
              </Typography>
            </Box>
            <List dense>
              {searchHistory.slice(0, 5).map((h, i) => {
                const idx = (jumpTo ? 1 : 0) + i;
                return (
                  <ListItemButton
                    key={h.id || `${h.query}-${i}`}
                    selected={highlightIdx === idx}
                    onClick={() => handleActivate({ kind: 'history', data: h })}
                  >
                    <ListItemIcon>
                      <HistoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={h.query} />
                  </ListItemButton>
                );
              })}
            </List>
          </>
        )}

        {/* Results clustered by module */}
        {query && results.length > 0 && (
          <>
            {Object.entries(clusters).map(([mod, rows]) => {
              const startIdx = firstResultIdx + results.findIndex((r) => r === rows[0]);
              return (
                <Box key={mod}>
                  <Box
                    sx={{
                      px: 2,
                      pt: 2,
                      pb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {mod.toUpperCase()}
                    </Typography>
                    <Chip label={rows.length} size="small" variant="outlined" />
                  </Box>
                  <List dense>
                    {rows.map((r, i) => {
                      const flatPosition = firstResultIdx + results.indexOf(r);
                      return (
                        <ListItemButton
                          key={r.id}
                          selected={highlightIdx === flatPosition}
                          onClick={() => handleActivate({ kind: 'result', data: r })}
                        >
                          <ListItemIcon>{iconFor(r.result_type)}</ListItemIcon>
                          <ListItemText
                            primary={r.title}
                            secondary={r.content_preview || r.matched_content || r.category}
                            primaryTypographyProps={{ noWrap: true }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                          <Chip
                            label={r.category || r.result_type}
                            size="small"
                            variant="outlined"
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </>
        )}

        {/* Empty state */}
        {query && !isSearching && results.length === 0 && !jumpTo && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No results. Press <b>Enter</b> to open the full search page.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.default',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="↑↓" size="small" variant="outlined" />
          <Typography variant="caption" color="text.secondary">
            Navigate
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="↵" size="small" variant="outlined" />
          <Typography variant="caption" color="text.secondary">
            Open
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="Esc" size="small" variant="outlined" />
          <Typography variant="caption" color="text.secondary">
            Close
          </Typography>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Ctrl+K to open anywhere
        </Typography>
      </Box>
    </Dialog>
  );
};

export default SearchSpotlightModal;
